'use client'

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import Flag from 'react-flagkit'
import { GroupIcon, ImageIcon, LanguagesIcon, PlusCircleIcon, SaveIcon, XIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { FileSelector } from '@/components/partials/files'
import {
  CreateVehicleGroupParams,
  VehicleGroupTranslationParam,
  VehicleGroupTranslationsParam,
  VehicleGroupType
} from '@/types/vehicle-group'
import { LoadingIndicator, UnsavedChangesGuardDialog } from '@/components/partials'
import { FileType } from '@/types/file'
import { useMetaStore } from '@/store'
import { FormModeEnum } from '@/enums'
import { setValidationErrors } from '@/lib/utils'
import { useScrollToError, useUnsavedChangesGuard } from '@/hooks'

type PropsType = {
  initialData?: VehicleGroupType | null
  onSubmit: (data: CreateVehicleGroupParams) => void
  onSubmitAndNew?: (data: CreateVehicleGroupParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type VehicleGroupFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

type FormData = {
  pictureId?: string
  translations: VehicleGroupTranslationsParam
}

const VehicleGroupForm = forwardRef<VehicleGroupFormRef, PropsType>(
  ({ initialData, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const { scrollToFirstError } = useScrollToError()

    const { metaState } = useMetaStore()
    // Wrap the metaState.response?.locales in its own useMemo to prevent unnecessary re-renders
    const localesData = useMemo(() => metaState.response?.locales, [metaState.response])
    const locales = useMemo(() => localesData || [], [localesData])

    // State for picture
    const [picture, setPicture] = useState<FileType | null>(initialData?.picture ?? null)

    const getCountryCodeFromLocale = (localeCode: string): string => {
      const parts = localeCode.split('-')
      return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
    }

    const hasLocaleError = (localeCode: string, field: keyof VehicleGroupTranslationParam): boolean => {
      return !!formErrors.translations?.[localeCode]?.[field]
    }

    const schema = useMemo(() => {
      const translationsSchema = {} as Record<string, z.ZodObject<{ title: z.ZodString }>>

      locales.forEach(locale => {
        translationsSchema[locale.fullCode] = z.object({
          title: z.string().min(1, t('not_blank'))
        })
      })

      return z.object({
        pictureId: z.string().optional(),
        translations: z.object(translationsSchema)
      })
    }, [locales, t])

    const getDefaultValues = useCallback((): FormData => {
      const defaultTranslations: VehicleGroupTranslationsParam = {}

      locales.forEach(locale => {
        // Ensure values are always strings, never undefined
        const translation = initialData?.translations?.[locale.fullCode]
        defaultTranslations[locale.fullCode] = {
          title: translation?.title ?? ''
        }
      })

      return {
        pictureId: initialData?.picture?.id ?? '',
        translations: defaultTranslations
      }
    }, [locales, initialData])

    type SchemaType = z.infer<typeof schema>

    const form = useForm<SchemaType>({
      resolver: zodResolver(schema),
      defaultValues: getDefaultValues() as SchemaType,
      mode: 'onChange'
    })

    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    // Expose handleValidationErrors to parent via ref
    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
        scrollToFirstError()
      },
      reset: () => form.reset()
    }))

    // Update form values when initialData or locales change
    useEffect(() => {
      if (locales.length > 0) {
        const newDefaultValues = getDefaultValues()
        form.reset(newDefaultValues as SchemaType)
      }
    }, [getDefaultValues, locales, form])

    const formErrors = form.formState.errors

    const handlePictureChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setPicture(null)
        form.setValue('pictureId', '', { shouldValidate: true })
      } else if ('length' in files) {
        setPicture(files[0])
        form.setValue('pictureId', files[0]?.id || '', { shouldValidate: true })
      } else {
        setPicture(files)
        form.setValue('pictureId', files?.id || '', { shouldValidate: true })
      }
    }

    const handleSubmit = (data: SchemaType) => {
      const submissionData: CreateVehicleGroupParams = {
        translations: {}
      }

      // In CREATE mode: don't send pictureId if no picture selected
      // In UPDATE mode: send empty string if no picture selected
      if (mode === FormModeEnum.CREATE) {
        if (picture?.id) {
          submissionData.pictureId = picture.id
        }
        // If no picture, don't add pictureId at all (undefined)
      } else {
        // UPDATE mode: always send pictureId, empty string if no picture
        submissionData.pictureId = picture?.id || ''
      }

      // Add translations
      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          title: data.translations[locale.fullCode]?.title || ''
        }
      })

      onSubmit(submissionData)
    }

    const handleSubmitAndNew = (data: SchemaType) => {
      const submissionData: CreateVehicleGroupParams = {
        translations: {}
      }

      // In CREATE mode: don't send pictureId if no picture selected
      // In UPDATE mode: send empty string if no picture selected
      if (mode === FormModeEnum.CREATE) {
        if (picture?.id) {
          submissionData.pictureId = picture.id
        }
        // If no picture, don't add pictureId at all (undefined)
      } else {
        // UPDATE mode: always send pictureId, empty string if no picture
        submissionData.pictureId = picture?.id || ''
      }

      // Add translations
      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          title: data.translations[locale.fullCode]?.title || ''
        }
      })

      onSubmitAndNew?.(submissionData)
    }

    if (!metaState.response?.locales) {
      return <LoadingIndicator />
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(
            isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit,
            scrollToFirstError
          )}
          className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GroupIcon className="h-5 w-5" />
                {mode === FormModeEnum.CREATE ? t('create_vehicle_group') : t('edit_vehicle_group')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Translations */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <LanguagesIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{t('translations')}</h3>
                </div>

                {locales.length > 0 ? (
                  <Tabs
                    defaultValue={locales[0].fullCode}
                    className="w-full"
                    onValueChange={value => {
                      setTimeout(() => {
                        const input = document.querySelector(`[name="translations.${value}.title"]`) as HTMLInputElement
                        if (input) {
                          input.focus()
                        }
                      }, 100)
                    }}>
                    <TabsList className="min-h-10.5 w-full">
                      {locales.map(locale => {
                        const hasError = hasLocaleError(locale.fullCode, 'title')

                        return (
                          <TabsTrigger
                            key={locale.fullCode}
                            value={locale.fullCode}
                            className={`relative flex items-center gap-2 ${
                              hasError ? 'text-destructive border-destructive' : ''
                            }`}>
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={16} />
                            <span className="hidden sm:inline">{locale.name}</span>
                            {hasError && (
                              <div className="bg-destructive absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full" />
                            )}
                          </TabsTrigger>
                        )
                      })}
                    </TabsList>

                    {locales.map(locale => {
                      const hasTitleError = hasLocaleError(locale.fullCode, 'title')

                      return (
                        <TabsContent
                          key={locale.fullCode}
                          value={locale.fullCode}
                          forceMount
                          className="mt-6 space-y-4 data-[state=inactive]:hidden">
                          <div className="flex items-center gap-2">
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={20} />
                            <h4 className="text-lg font-semibold">{locale.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {locale.fullCode}
                            </Badge>
                            {hasTitleError && (
                              <Badge variant="destructive" className="animate-pulse text-xs">
                                {t('error')}
                              </Badge>
                            )}
                          </div>

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.title`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel required>{t('title')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('title')}
                                    className={`${hasTitleError ? 'border-destructive focus:border-destructive' : ''}`}
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      )
                    })}
                  </Tabs>
                ) : (
                  <div className="py-8 text-center">
                    <p>{t('no_locales_available')}</p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Picture */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name={'pictureId'}
                  render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                />
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('picture')}</h3>
                </div>
                <div>
                  <FileSelector
                    multiple={false}
                    onlyImages={true}
                    optimizeImageSelection={true}
                    value={picture}
                    onChange={handlePictureChange}
                    buttonLabel={t('select_picture')}
                    buttonLabelChange={t('change_picture')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={initialData?.picture?.id}
                  />
                  {formErrors.pictureId && (
                    <p className="text-destructive mt-2 text-sm">
                      {String(formErrors.pictureId.message || t('required'))}
                    </p>
                  )}
                </div>
              </div>

              <Separator />

              {/* Form actions */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                  <XIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleSubmit, scrollToFirstError)()}
                      disabled={isLoading}>
                      <SaveIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">{isLoading ? t('saving') : t('save')}</span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading}>
                    {isCreateModeWithSubmitAndNew ? (
                      <PlusCircleIcon className="h-4 w-4" />
                    ) : (
                      <SaveIcon className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">
                      {isLoading ? t('saving') : isCreateModeWithSubmitAndNew ? t('save_and_add_new') : t(mode)}
                    </span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <UnsavedChangesGuardDialog
            open={isNavigationBlocked}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
          />
        </form>
      </Form>
    )
  }
)

VehicleGroupForm.displayName = 'VehicleGroupForm'

export default VehicleGroupForm
